// Custom Dashboard — Sales Trend. Same shape as TrendAreaChart but the X-axis
// tick/tooltip label format adapts to the backend's auto-picked granularity
// (daily/weekly/monthly), which TrendAreaChart's fixed "dd MMM" format can't
// represent — kept as its own component instead of reworking the shared one
// that Reports > Dashboard's Customer Growth/Revenue Trend also rely on.
import * as React from "react";
import { format } from "date-fns";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export interface CustomSalesTrendPoint {
  date: string;
  label: string;
  amount: number;
}

export type SalesTrendGranularity = "daily" | "weekly" | "monthly";

export interface CustomSalesTrendChartProps {
  data: CustomSalesTrendPoint[];
  granularity: SalesTrendGranularity;
  valueFormatter?: (v: number) => string;
}

function tickLabel(point: CustomSalesTrendPoint, granularity: SalesTrendGranularity) {
  try {
    if (granularity === "weekly") return format(new Date(point.date), "dd MMM");
    if (granularity === "monthly") return format(new Date(point.date), "MMM yyyy");
    return format(new Date(point.date), "dd MMM");
  } catch {
    return point.label;
  }
}

function tooltipLabel(point: CustomSalesTrendPoint, granularity: SalesTrendGranularity) {
  try {
    return tickLabel(point, granularity);
  } catch {
    return point.label;
  }
}

// Picks a "nice" step (1/2/5 x a power of 10) so the Y-axis lands on round
// numbers instead of dividing the raw max into equal-but-ugly fractions, and
// clamps ticks to what the data actually spans instead of a fixed count.
function niceYAxis(maxValue: number, tickCount = 5) {
  if (!Number.isFinite(maxValue) || maxValue <= 0) {
    return { domain: [0, 1] as [number, number], ticks: [0, 1] };
  }
  const rawStep = maxValue / Math.max(tickCount - 1, 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const residual = rawStep / magnitude;
  const niceStep = (residual > 5 ? 10 : residual > 2 ? 5 : residual > 1 ? 2 : 1) * magnitude;
  const niceMax = Math.ceil(maxValue / niceStep) * niceStep;
  const ticks: number[] = [];
  for (let t = 0; t <= niceMax + niceStep / 2; t += niceStep) ticks.push(Math.round(t));
  return { domain: [0, niceMax] as [number, number], ticks };
}

// Thins out X-axis labels once there are more points than fit legibly,
// instead of always rendering one label per data point.
function xAxisInterval(pointCount: number, maxLabels = 8) {
  if (pointCount <= maxLabels) return 0;
  return Math.ceil(pointCount / maxLabels) - 1;
}

export function CustomSalesTrendChart({ data, granularity, valueFormatter }: CustomSalesTrendChartProps) {
  const fmt = valueFormatter ?? ((v: number) => String(v));
  const { domain: yDomain, ticks: yTicks } = React.useMemo(
    () => niceYAxis(Math.max(0, ...data.map((d) => Number(d.amount) || 0))),
    [data]
  );
  const interval = React.useMemo(() => xAxisInterval(data.length), [data.length]);
  // Recharts' tickFormatter index argument is the position among the ticks it
  // actually renders once `interval` skips some, not the position in `data` —
  // indexing data[i] there grabs the wrong point once ticks are thinned out,
  // showing a label for a different hour/day than the one at that pixel.
  // Looking the point up by its own category value sidesteps that entirely.
  const pointByLabel = React.useMemo(() => {
    const map = new Map<string, CustomSalesTrendPoint>();
    data.forEach((d) => map.set(d.label, d));
    return map;
  }, [data]);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="customTrendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
        <XAxis
          dataKey="label"
          tickFormatter={(value) => {
            const point = pointByLabel.get(value);
            return point ? tickLabel(point, granularity) : value;
          }}
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          axisLine={false}
          tickLine={false}
          interval={interval}
        />
        <YAxis
          domain={yDomain}
          ticks={yTicks}
          tickFormatter={(v) => fmt(v)}
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          axisLine={false}
          tickLine={false}
          width={56}
        />
        <Tooltip
          formatter={(v: number) => fmt(v)}
          labelFormatter={(_, payload) => (payload?.[0]?.payload ? tooltipLabel(payload[0].payload, granularity) : "")}
          contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
        />
        <Area type="monotone" dataKey="amount" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#customTrendFill)" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default CustomSalesTrendChart;
